import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, ClockIcon } from '../../components/ui/Icons';
import { PaymentMethod } from '../../models';
import { usePaymentSettings } from '../../hooks/usePaymentSettings';
import { formatReservationDeadline, parseInstant } from '../../utils/reservation';
import { formatCurrency } from '../../utils/currency';
import { buildOrderFollowUpWhatsAppUrl } from '../../utils/whatsapp';
import { WhatsAppIcon } from './CheckoutIcons';
import { CreateAccountCard } from './CreateAccountCard';
import { OrderConfirmation } from './checkoutStorage';
import { OUTLINE_LINK_BUTTON_CLASS, PRIMARY_LINK_BUTTON_CLASS } from './styles';

const MS_PER_MINUTE = 60_000;

function getFirstName(fullName?: string): string | null {
  const first = fullName?.trim().split(/\s+/)[0];
  return first ? first : null;
}

interface OrderSuccessScreenProps {
  orderId: number;
  confirmation: OrderConfirmation | null;
  isAuthenticated: boolean;
}

function PixReservationNotice({ orderedAt }: { orderedAt: Date }) {
  const { settings } = usePaymentSettings();
  const reservationMinutes = settings?.pixReservationMinutes ?? 0;
  if (reservationMinutes <= 0) return null;

  const deadline = new Date(orderedAt.getTime() + reservationMinutes * MS_PER_MINUTE);

  return (
    <div className="w-full max-w-sm flex items-start gap-3 text-left bg-champagne/60 border border-champagne-dark rounded-xl p-4 mb-6">
      <ClockIcon className="w-5 h-5 shrink-0 text-chocolate-dark mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-text-dark">
          Suas peças ficam reservadas até {formatReservationDeadline(deadline)} enquanto vocês combinam o pagamento.
        </p>
      </div>
    </div>
  );
}

function OrderRecap({ confirmation }: { confirmation: OrderConfirmation }) {
  const items = confirmation.items ?? [];
  if (items.length === 0 && confirmation.total == null) return null;

  return (
    <section
      aria-label="Resumo do pedido"
      className="w-full max-w-sm text-left bg-white border border-champagne-dark rounded-xl p-4 mb-6"
    >
      <h2 className="text-sm font-bold text-chocolate-dark mb-2.5">Resumo do pedido</h2>
      {items.length > 0 && (
        <ul className="flex flex-col gap-2 mb-3">
          {items.map((item) => (
            <li key={`${item.name}-${item.variant}`} className="flex justify-between gap-3 text-sm">
              <span className="min-w-0 break-words">
                <span className="block font-semibold text-text-dark">{item.name}</span>
                {item.variant && <span className="block text-xs text-text-medium">{item.variant}</span>}
              </span>
              <span className="text-text-medium flex-shrink-0">{item.quantity}x</span>
            </li>
          ))}
        </ul>
      )}
      {confirmation.total != null && (
        <div className="border-t border-champagne-dark/40 pt-3">
          <div className="flex justify-between items-baseline gap-2">
            <span className="text-sm font-medium text-text-dark">Total dos produtos</span>
            <span className="text-lg font-bold text-text-dark">R$ {formatCurrency(confirmation.total)}</span>
          </div>
          <p className="text-xs text-text-medium mt-0.5">Frete: a vendedora confirma pelo WhatsApp</p>
        </div>
      )}
    </section>
  );
}

export function OrderSuccessScreen({ orderId, confirmation, isAuthenticated }: OrderSuccessScreenProps) {
  const hasPix = confirmation?.paymentMethods.includes(PaymentMethod.Pix) ?? false;
  const orderedAt = parseInstant(confirmation?.orderedAt) ?? new Date();
  const [accountCreated, setAccountCreated] = useState(false);
  const sellerFirstName = getFirstName(confirmation?.sellerName);
  const sellerLabel = sellerFirstName ? `A vendedora ${sellerFirstName}` : 'A vendedora';
  const whatsAppUrl = confirmation?.whatsAppUrl ?? buildOrderFollowUpWhatsAppUrl(orderId);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 text-center flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-champagne flex items-center justify-center mb-4 text-chocolate-dark">
          <CheckCircleIcon className="w-9 h-9" />
        </div>

        <h1 className="text-xl font-bold text-text-dark mb-1">
          {confirmation ? `Pedido nº ${orderId} recebido!` : `Pedido nº ${orderId}`}
        </h1>
        <p className="text-base text-text-medium mb-6 max-w-sm">
          {confirmation
            ? `Falta só um passo: mande o pedido para ${sellerLabel.toLowerCase()} no WhatsApp. Ela confirma o frete com você e combina o pagamento.`
            : `Mande o pedido para ${sellerLabel.toLowerCase()} no WhatsApp para ela confirmar o frete e combinar o pagamento. Se não encontrar nosso contato, fale com a loja informando o número do pedido.`}
        </p>

        {confirmation && <OrderRecap confirmation={confirmation} />}

        {confirmation && hasPix && <PixReservationNotice orderedAt={orderedAt} />}

        <div className="flex flex-col items-center gap-3 w-full sm:w-auto">
          <a
            href={whatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${PRIMARY_LINK_BUTTON_CLASS} w-full sm:w-auto px-8`}
          >
            <WhatsAppIcon className="w-5 h-5" />
            Enviar pedido para a vendedora
          </a>
          <Link to="/" className={`${OUTLINE_LINK_BUTTON_CLASS} w-full sm:w-auto px-8`}>
            Voltar para a loja
          </Link>
          {isAuthenticated && !accountCreated && (
            <Link to="/pedidos" className={`${OUTLINE_LINK_BUTTON_CLASS} w-full sm:w-auto`}>
              Meus pedidos
            </Link>
          )}
        </div>
      </div>

      {(!isAuthenticated || accountCreated) && (
        <CreateAccountCard
          guest={confirmation?.guest}
          created={accountCreated}
          onCreated={() => setAccountCreated(true)}
        />
      )}
    </div>
  );
}
